import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebaseConfig';

/**
 * File upload utility for MMS media files using Firebase Storage
 */

/**
 * Uploads a file to Firebase Storage and returns a public URL
 * @param {File} file - The file to upload
 * @param {string} userId - User ID for organizing files
 * @returns {Promise<string>} - The public URL of the uploaded file
 */
export const uploadFile = async (file, userId = 'anonymous') => {
  try {
    // Create a unique filename
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2);
    const fileExtension = file.name.split('.').pop();
    const fileName = `${timestamp}-${randomId}.${fileExtension}`;
    
    // Create storage path: mms-media/{userId}/{fileName}
    const storagePath = `mms-media/${userId}/${fileName}`;
    const storageRef = ref(storage, storagePath);
    
    console.log(`Uploading file to Firebase Storage: ${storagePath}`);
    
    // Upload the file
    const snapshot = await uploadBytes(storageRef, file, {
      contentType: file.type,
      customMetadata: {
        originalName: file.name,
        uploadedAt: new Date().toISOString(),
        userId: userId
      }
    });
    
    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    console.log(`File uploaded successfully: ${downloadURL}`);
    return downloadURL;
    
  } catch (error) {
    console.error('Error uploading file to Firebase Storage:', error);
    throw new Error(`Failed to upload file: ${error.message}`);
  }
};

/**
 * Deletes a file from Firebase Storage
 * @param {string} downloadURL - The download URL of the file to delete
 * @returns {Promise<void>}
 */
export const deleteFile = async (downloadURL) => {
  try {
    // Extract the file path from the download URL
    const url = new URL(downloadURL);
    const pathMatch = url.pathname.match(/\/v0\/b\/[^\/]+\/o\/(.+)/);
    if (!pathMatch) {
      throw new Error('Invalid Firebase Storage URL');
    }
    
    const filePath = decodeURIComponent(pathMatch[1]);
    const fileRef = ref(storage, filePath);
    
    await deleteObject(fileRef);
    console.log(`File deleted successfully: ${filePath}`);
  } catch (error) {
    console.error('Error deleting file from Firebase Storage:', error);
    throw new Error(`Failed to delete file: ${error.message}`);
  }
};

/**
 * Uploads multiple files and returns their public URLs
 * @param {File[]} files - Array of files to upload
 * @param {string} userId - User ID for organizing files
 * @returns {Promise<string[]>} - Array of public URLs
 */
export const uploadFiles = async (files, userId = 'anonymous') => {
  try {
    const uploadPromises = files.map(file => uploadFile(file, userId));
    return await Promise.all(uploadPromises);
  } catch (error) {
    console.error('Error uploading files:', error);
    throw new Error('Failed to upload files');
  }
};

/**
 * Validates if a file is suitable for MMS
 * @param {File} file - The file to validate
 * @returns {object} - Validation result with isValid boolean and error message
 */
export const isValidMmsFile = (file) => {
  // Check if file exists
  if (!file) {
    return { isValid: false, error: 'No file provided' };
  }
  
  // Check file type
  const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const allowedVideoTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/webm', 'video/quicktime'];
  const allowedTypes = [...allowedImageTypes, ...allowedVideoTypes];
  
  if (!allowedTypes.includes(file.type.toLowerCase())) {
    return { 
      isValid: false, 
      error: 'File type not supported. Please use JPG, PNG, GIF, WebP, MP4, MOV, AVI, or WebM files.' 
    };
  }
  
  // Check file size (10MB limit for MMS)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return { 
      isValid: false, 
      error: `File size (${sizeMB}MB) exceeds the 10MB limit for MMS.` 
    };
  }
  
  // Check minimum file size (avoid empty files)
  if (file.size < 100) {
    return { 
      isValid: false, 
      error: 'File appears to be empty or corrupted.' 
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Gets the display name for a file type
 * @param {File} file - The file
 * @returns {string} - Display name for the file type
 */
export const getFileTypeDisplay = (file) => {
  if (file.type.startsWith('image/')) {
    return 'Image';
  } else if (file.type.startsWith('video/')) {
    return 'Video';
  }
  return 'File';
};

/**
 * Formats file size in human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted size (e.g., "2.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * Compresses an image file (basic implementation)
 * @param {File} file - Image file to compress
 * @param {number} maxWidth - Maximum width for the compressed image
 * @param {number} quality - Compression quality (0-1)
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImage = async (file, maxWidth = 1920, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      resolve(file); // Return original if not an image
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
      const newWidth = img.width * ratio;
      const newHeight = img.height * ratio;

      // Set canvas dimensions
      canvas.width = newWidth;
      canvas.height = newHeight;

      // Draw and compress
      ctx.drawImage(img, 0, 0, newWidth, newHeight);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            });
            resolve(compressedFile);
          } else {
            resolve(file); // Return original if compression failed
          }
        },
        file.type,
        quality
      );
    };

    img.onerror = () => resolve(file); // Return original if loading failed
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Uploads a file with optional compression
 * @param {File} file - The file to upload
 * @param {string} userId - User ID for organizing files
 * @param {object} options - Upload options (compress, maxWidth, quality)
 * @returns {Promise<string>} - The public URL of the uploaded file
 */
export const uploadFileWithCompression = async (file, userId = 'anonymous', options = {}) => {
  const { compress = true, maxWidth = 1920, quality = 0.8 } = options;
  
  let fileToUpload = file;
  
  // Compress image if enabled and file is an image
  if (compress && file.type.startsWith('image/')) {
    try {
      fileToUpload = await compressImage(file, maxWidth, quality);
      console.log(`Image compressed: ${formatFileSize(file.size)} → ${formatFileSize(fileToUpload.size)}`);
    } catch (error) {
      console.warn('Image compression failed, uploading original:', error);
      fileToUpload = file;
    }
  }
  
  return uploadFile(fileToUpload, userId);
};
